import React from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import colors from '../../style/colors';
import Icon from '../icon';

// components
import AvatarBadge, { AvatarBadgeProps } from './avatar-badge';
import { sizes, variants, badgeRatios } from './style';

export const avatarUserVariants = [
  'darkIndigo',
  'darkPurple',
  'sofiaPink',
  'darkRed',
  'darkOrange',
  'eggYolk',
  'saladish',
  'grassGreen',
  'navy',
  'brightBlue',
] as const;

/***
 * Helpers
 ***/
const getColorFromUserId = (userId: number | string): (typeof avatarUserVariants)[number] => {
  const idAsString = `${userId}`;
  const digit = parseInt(idAsString.charAt(idAsString.length - 1));
  const color = digit % avatarUserVariants.length;
  return avatarUserVariants[color];
};

export const isStringAlphaNumeric = (string: string): boolean => {
  if (!string) {
    return false;
  }

  return /^[a-z\u0590-\u05fe]+$/i.test(string);
};

export const getInitialsFromFullName = (name: string, size: AvatarProps['size'] = 'lg') => {
  const iconSize = sizes[size].fallbackIconSize;

  if (!name) {
    return <Icon name={'PERSON'} size={iconSize} variant="inherit" />;
  }

  const trimmedName = name?.trim?.();

  const names = trimmedName.split(' ');
  const initials: string[] = [];

  names.map(name => {
    const firstLetter = name.charAt(0);
    if (isStringAlphaNumeric(name.charAt(0))) {
      initials.push(firstLetter);
    }
  });

  if (initials.length === 0) {
    return <Icon name={'PERSON'} size={iconSize} variant="inherit" />;
  }

  return initials.slice(0, 2).join('');
};

/**
 * Types
 */
type ImageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface AvatarProps {
  photo?: string;
  name?: string;
  className?: string;
  children?: React.ReactNode;
  markup?: React.ReactNode;
  text?: string | React.ReactElement;
  'data-id'?: number | string;
  initials?: string;
  style?: React.CSSProperties;
  size?: keyof typeof sizes;
  variant?: keyof typeof variants;
  flexShrink?: 0 | 1;
  flexGrow?: 0 | 1;
  userId?: number | string;
  'aria-hidden'?: boolean;
}

const useImageLoadingStatus = (src?: string) => {
  const [loadingStatus, setLoadingStatus] = React.useState<ImageLoadingStatus>('idle');

  // sometimes backend returns image with query params that are required, but changes on every new call to BE.
  // in this case we wanna change and re render the avatar only when the url changes and not when query params
  // change, which is on every new call to BE

  const srcWithoutQueryParams = src?.split?.('?')?.[0];

  React.useEffect(() => {
    if (!src) {
      setLoadingStatus('error');
      return;
    }

    let isMounted = true;
    const image = new window.Image();

    const updateStatus = (status: ImageLoadingStatus) => () => {
      if (!isMounted) {
        return;
      }
      setLoadingStatus(status);
    };

    setLoadingStatus('loading');
    image.onload = updateStatus('loaded');
    image.onerror = updateStatus('error');
    image.src = src;

    return () => {
      isMounted = false;
    };
  }, [srcWithoutQueryParams]);

  return loadingStatus;
};

/**
 * Component
 */

const AvatarComponent = React.memo(
  ({
    photo,
    name,
    className,
    children,
    markup,
    text,
    'data-id': dataId,
    initials,
    style,
    'aria-hidden': ariaHidden = false,
    size = 'lg',
  }: AvatarProps) => {
    const imageLoadingStatus = useImageLoadingStatus(photo);
    const isBroken = imageLoadingStatus === 'error';

    const parsedInitials = initials || getInitialsFromFullName(name || '', size);
    const shouldShowInitials =
      ((isBroken || (!photo && !(typeof parsedInitials === 'string' && parsedInitials.length === 0))) && !markup) ||
      text;
    const isInitialsEmpty = React.isValidElement(parsedInitials);
    const shouldShowImage = !isBroken && photo;

    return (
      <span className={classNames('box-avatar', className)} data-id={dataId} data-name={'avatar'} style={style}>
        <span className={'content-container'}>
          {shouldShowImage && (
            <img
              alt={name}
              aria-hidden={ariaHidden}
              data-testid={'photo'}
              src={photo}
              className={classNames({ shown: imageLoadingStatus === 'loaded' }, 'avatar-image')}
            />
          )}
          {shouldShowInitials && (
            <span data-testid={'initials'} className={'initials'}>
              {text || (
                <span
                  className={classNames({ 'empty-initials': isInitialsEmpty })}
                  role={!shouldShowImage && name ? 'img' : undefined}
                  aria-label={shouldShowImage ? undefined : name}
                  aria-hidden={ariaHidden}
                >
                  {parsedInitials}
                </span>
              )}
            </span>
          )}
          {markup}
        </span>
        {children}
      </span>
    );
  },
);

const StyledAvatar = styled(AvatarComponent)<Omit<AvatarProps, 'size'> & { size: keyof typeof sizes }>`
  width: ${props => sizes[props.size]?.width};
  height: ${props => sizes[props.size]?.height};
  color: white;
  letter-spacing: -0.0625rem;
  border-radius: 50%;

  text-transform: uppercase;
  font-size: 1.5625rem;
  text-align: center;
  line-height: 3.75rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: ${props => props.flexShrink};

  ${props =>
    props.flexGrow &&
    css`
      flex-grow: ${props.flexGrow};
    `};

  background: ${colors.gray400};

  ${props => (props.userId ? variants[getColorFromUserId(props.userId)] : variants[props.variant || 'gray400'])};

  .content-container {
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    &,
    & > .avatar-image {
      flex-grow: 1;
      height: 100%; //IE 11 fix for min-height and flex;
      vertical-align: initial;
      border-radius: 50%;
      max-height: 100%;
      max-width: 100%;
      min-width: 100%;
      min-height: 100%;
      object-fit: cover;
    }

    .avatar-image {
      display: block;
      vertical-align: initial;
      max-height: 100%;
      max-width: 100%;
      min-width: 100%;
      object-fit: cover;
      opacity: 0;

      &.shown {
        opacity: 1;
      }
    }
  }

  .initials {
    color: inherit;
    letter-spacing: 0;
    font-weight: 500;
    font-size: ${props => sizes[props.size]?.fontSize};

    .empty-initials {
      user-select: none;
      color: ${colors.white};
    }
  }

  ${AvatarBadge} {
    width: calc(${props => sizes[props.size].width} / ${badgeRatios.size});
    height: calc(${props => sizes[props.size].height} / ${badgeRatios.size});

    i {
      font-size: calc(${props => sizes[props.size].fontSize} / ${badgeRatios.icon});
    }
  }
`;

const Avatar: React.FC<AvatarProps> & {
  Badge: React.FunctionComponent<AvatarBadgeProps>;
} = ({ variant = 'gray400', size = 'lg', ...rest }: AvatarProps) => (
  <StyledAvatar flexShrink={0} size={size} variant={variant} {...rest} />
);

Avatar.Badge = AvatarBadge;

export default Avatar;
